<template>
  <div ui-view="" class="app-body" id="view">
    <page-header title="작업허가서 조회" description="작업허가서 신청 내역을 조회합니다." />

    <div class="page-body ph-30">
      <!-- 검색 영역 -->
      <div class="pt-20 pb-20">
        <div class="row">
          <div class="search-item">
            <div class="input-group">
              <span class="input-group-addon title">신청 기간</span>
              <DatePicker class="form-control"
                          type="date"
                          range
                          v-model="dateRange"
                          style="width:240px; font-size: 12px;"
                          :clearable="true"
                          :editable="false"
                          input-class="calendar-input">
                <i slot="icon-calendar" class="icon-calendar" style="color:#222;cursor:pointer"></i>
              </DatePicker>
            </div>
          </div>

          <div class="search-item">
            <div class="input-group">
              <dropdown title="상태" v-model="search.status"
                        :datasource="statusList"
                        displayName="name" valueName="value"
                        :isSelectionFirstData="search.status === null || search.status === ''" />
            </div>
          </div>

          <div class="search-item">
            <div class="input-group">
              <dropdown title="사이트" v-model="search.idcCode"
                        :datasource="idcOptions"
                        displayName="name" valueName="value"
                        :isSelectionFirstData="!search.idcCode" />
            </div>
          </div>

          <div class="search-item">
            <div class="input-group">
              <span class="input-group-addon title">일일허가서 작성일</span>
              <DatePicker class="form-control"
                          type="date"
                          v-model="dailyWorkDate"
                          style="width:140px; font-size: 12px;"
                          :clearable="true"
                          :editable="false"
                          input-class="calendar-input">
                <i slot="icon-calendar" class="icon-calendar" style="color:#222;cursor:pointer"></i>
              </DatePicker>
            </div>
          </div>
        </div>

        <div class="row mt-10">
          <div class="search-item">
            <div class="input-group">
              <span class="input-group-addon title">검색어</span>
              <input v-model="search.keyword" type="text" class="form-control"
                     placeholder="계획번호·작업명·업체명"
                     @keyup.enter="onSearch()">
            </div>
          </div>
          <div class="search-item">
            <button class="btn btn-md line-1" @click="resetSearch()">Reset</button>
          </div>
          <div class="search-item">
            <button class="btn btn-md blue ml-6" @click="onSearch()">검색</button>
          </div>
        </div>
      </div>

      <!-- 목록 -->
      <table class="tbl">
        <colgroup>
          <col width="auto">
          <col width="auto">
          <col width="auto">
          <col width="auto">
          <col width="auto">
          <col width="auto">
          <col width="auto">
          <col width="auto">
          <col width="auto">
          <col width="auto">
          <col width="auto">
          <col width="100px">
        </colgroup>
        <thead>
          <tr>
            <th>계획번호</th>
            <th>작업명</th>
            <th>업체명</th>
            <th>발주담당자</th>
            <th>작업 지휘자</th>
            <th>사이트</th>
            <th>작업 위치</th>
            <th>작업기간</th>
            <th>인원</th>
            <th>상태</th>
            <th>신청일시</th>
            <th class="text-center">일일허가서</th>
          </tr>
        </thead>
        <tbody v-if="rows.length === 0">
          <tr>
            <td colspan="12" class="text-center text-888 pv-30">조회 결과가 없습니다.</td>
          </tr>
        </tbody>
        <tbody v-else>
          <template v-for="r in rows">
            <tr :key="r.planId">
              <td>
                <a href="javascript:void(0)" class="under-line" @click.stop="goDetail(r)">{{ r.planId }}</a>
              </td>
              <td class="ellipsis">{{ r.planName || '-' }}</td>
              <td class="ellipsis">{{ r.companyName || '-' }}</td>
              <td class="ellipsis" :title="r.createdBy || ''">{{ ordererOf(r) }}</td>
              <td class="ellipsis">{{ r.workManager || '-' }}</td>
              <td>{{ idcNameOf(r.idcCode) }}</td>
              <td class="wp-loc-cell">
                <template v-if="locationCount(r) === 0">-</template>
                <div v-else class="wp-loc-row">
                  <div class="wp-loc-text">
                    <div v-if="!isLocationExpanded(r)" class="ellipsis">{{ locationSummary(r) }}</div>
                    <ul v-else class="wp-loc-list">
                      <li v-for="(name, i) in allLocationNames(r)" :key="'loc-'+r.planId+'-'+i">{{ name }}</li>
                    </ul>
                  </div>
                  <button v-if="locationCount(r) > 1"
                          type="button"
                          class="btn btn-s line-3 wp-loc-toggle"
                          :title="isLocationExpanded(r) ? '접기' : '펼쳐보기'"
                          @click.stop="toggleLocation(r)">
                    <i :class="isLocationExpanded(r) ? 'icon-arrow-line-up' : 'icon-arrow-line-down'"></i>
                  </button>
                </div>
              </td>
              <td>{{ formatPeriod(r.fromDate, r.toDate) }}</td>
              <td>{{ r.workPeopleCount != null ? r.workPeopleCount + '명' : '-' }}</td>
              <td>
                <span class="tag" :class="statusTagClass(r.status)">{{ statusLabel(r.status) }}</span>
              </td>
              <td>{{ formatTs(r.submittedDate || r.createDate) }}</td>
              <td class="text-center">
                <button type="button" class="btn btn-s line-3 wp-daily-toggle"
                        :title="isDailyExpanded(r) ? '접기' : '열기'" @click.stop="toggleDaily(r)">
                  <span class="wp-daily-cnt">{{ r.dailyCount != null ? r.dailyCount : 0 }}건</span>
                  <i :class="isDailyExpanded(r) ? 'icon-arrow-line-up' : 'icon-arrow-line-down'"></i>
                </button>
              </td>
            </tr>
            <tr v-if="isDailyExpanded(r)" :key="r.planId + '-daily'" class="wp-daily-row">
              <td colspan="12" class="wp-daily-cell">
                <div class="wp-daily-title">일일작업허가서
                  <span class="text-888 ml-5" v-if="dailyMap[r.planId]">({{ dailyMap[r.planId].length }}건)</span>
                </div>
                <div v-if="dailyLoading[r.planId]" class="text-888 pv-10">불러오는 중...</div>
                <div v-else-if="!(dailyMap[r.planId] && dailyMap[r.planId].length)" class="text-888 pv-10">
                  작성된 일일작업허가서가 없습니다.
                </div>
                <table v-else class="tbl wp-daily-tbl">
                  <colgroup>
                    <col width="auto"><col width="110px"><col width="90px"><col width="auto"><col width="auto">
                    <col width="120px"><col width="80px"><col width="80px"><col width="140px">
                  </colgroup>
                  <thead>
                    <tr>
                      <th>일일허가서 번호</th><th>작업일자</th><th>차수</th><th>작업종류</th><th>작업위치</th>
                      <th>작업시간</th><th>종류수</th><th>상태</th><th>작성/제출일</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="d in dailyMap[r.planId]" :key="d.dailyPermitId"
                        style="cursor:pointer" :class="{ 'wp-daily-superseded': d._revTotal > 1 && !d._isLatestRev }"
                        @click="openDailyDetail(d)">
                      <td><a href="javascript:void(0)" class="under-line" @click.stop="openDailyDetail(d)">{{ d.dailyPermitId }}</a></td>
                      <td>{{ formatDay(d.workDate) }}</td>
                      <td>
                        <span>{{ d._rev }}차</span>
                        <span v-if="d._revTotal > 1 && d._isLatestRev" class="tag success ml-3" style="font-size:9px;">최신</span>
                        <span v-else-if="d._revTotal > 1" class="tag line-type ml-3" style="font-size:9px;">이전</span>
                      </td>
                      <td class="ellipsis" :title="dailyTypeLabels(d)">{{ dailyTypeLabels(d) }}</td>
                      <td class="ellipsis" :title="dailyLocation(d)">{{ dailyLocation(d) }}</td>
                      <td>{{ dailyTimeSlots(d) }}</td>
                      <td>{{ dailyTypeCount(d) }}</td>
                      <td><span class="tag" :class="statusTagClass(d.status)">{{ statusLabel(d.status) }}</span></td>
                      <td>{{ formatTs(d.submittedDate || d.createDate) }}</td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <!-- 페이지네이션 (NDKC pagination v1.5 패턴) -->
      <div class="mt-20" v-if="total > 0">
        <ul class="pagination" style="justify-content:center;">
          <li :class="{ disabled: page <= 1 }">
            <a href="javascript:void(0)" @click="goPage(1)"><i class="icon-arrow-line-left-first"></i></a>
          </li>
          <li :class="{ disabled: page <= 1 }">
            <a href="javascript:void(0)" @click="goPage(page - 10)"><i class="icon-arrow-2line-left"></i></a>
          </li>
          <li :class="{ disabled: page <= 1 }">
            <a href="javascript:void(0)" @click="goPage(page - 1)"><i class="icon-arrow-line-left-centered"></i></a>
          </li>
          <li v-for="p in pageNumbers" :key="'pg-'+p" :class="{ active: p === page }">
            <a href="javascript:void(0)" @click="goPage(p)">{{ p }}</a>
          </li>
          <li :class="{ disabled: page >= totalPages }">
            <a href="javascript:void(0)" @click="goPage(page + 1)"><i class="icon-arrow-line-right-centered"></i></a>
          </li>
          <li :class="{ disabled: page >= totalPages }">
            <a href="javascript:void(0)" @click="goPage(page + 10)"><i class="icon-arrow-2line-right"></i></a>
          </li>
          <li :class="{ disabled: page >= totalPages }">
            <a href="javascript:void(0)" @click="goPage(totalPages)"><i class="icon-arrow-line-right-end"></i></a>
          </li>
        </ul>
        <div class="text-center text-888 mt-5" style="font-size:12px;">전체 {{ total }}건</div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import PageHeader from '@/layout/PageHeader.vue';
import Dropdown from '@/components/Dropdown.vue';

const DEFAULT_PAGE_SIZE = 20;

export default {
  name: 'WorkPermitList',
  components: { PageHeader, Dropdown },
  data() {
    return {
      dateRange: [],
      dailyWorkDate: '',
      appliedDailyWorkDate: null,
      search: {
        keyword: '',
        status: '',
        idcCode: ''
      },
      rows: [],
      total: 0,
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      loading: false,
      locationMap: {},
      idcOptions: [{ name: '전체', value: '' }],
      expandedLocations: {},
      expandedDaily: {},
      dailyMap: {},
      dailyLoading: {},
      statusList: [
        { name: '전체', value: '' },
        { name: '임시저장', value: 'DRAFT' },
        { name: '신청 완료(검토 대기)', value: 'SUBMITTED' },
        { name: 'NTREE 결재 중', value: 'NTREE_PENDING' },
        { name: '승인', value: 'APPROVED' },
        { name: '반려', value: 'REJECTED' }
      ]
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
    pageNumbers() {
      const total = this.totalPages;
      const current = this.page;
      const windowSize = 10;
      if (total <= windowSize) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }
      let start = Math.max(1, current - Math.floor(windowSize / 2));
      let end = start + windowSize - 1;
      if (end > total) {
        end = total;
        start = Math.max(1, end - windowSize + 1);
      }
      const out = [];
      for (let i = start; i <= end; i += 1) out.push(i);
      return out;
    }
  },
  mounted() {
    Promise.all([this.loadLocations(), this.fetch()]);
  },
  methods: {
    async loadLocations() {
      try {
        const res = await this.$http.get('/api/public/safety-location/idcList');
        const nodes = (res && res.data) || [];
        const map = {};
        const idcList = [{ name: '전체', value: '' }];
        const walk = (arr, depth) => {
          (arr || []).forEach((node) => {
            if (!node) return;
            if (node.isDeleteYN && node.isDeleteYN !== 'N') return;
            map[node.locationCode] = node.locationName;
            if (depth === 0) {
              idcList.push({ name: node.locationName, value: node.locationCode });
            }
            if (node.children && node.children.length) walk(node.children, depth + 1);
          });
        };
        walk(nodes, 0);
        this.locationMap = map;
        this.idcOptions = idcList;
      } catch (e) {
        console.log('safety-location 로드 실패', e);
      }
    },
    buildParams() {
      const params = {
        page: this.page,
        pageSize: this.pageSize
      };
      if (this.search.keyword) params.keyword = this.search.keyword;
      if (this.search.status) params.status = this.search.status;
      if (this.search.idcCode) params.idcCode = this.search.idcCode;
      if (this.dateRange && this.dateRange[0]) {
        params.fromDate = new Date(this.dateRange[0]).setHours(0, 0, 0, 0);
      }
      if (this.dateRange && this.dateRange[1]) {
        params.toDate = new Date(this.dateRange[1]).setHours(23, 59, 59, 999);
      }
      if (this.dailyWorkDate) {
        params.dailyWorkDate = new Date(this.dailyWorkDate).setHours(0, 0, 0, 0);
      }
      return params;
    },
    async fetch() {
      this.loading = true;
      // 필터 변경 후 재조회 시, 이전에 펼쳐둔 일일허가서 캐시가 새 조회조건과 어긋나지 않도록 초기화
      this.expandedDaily = {};
      this.dailyMap = {};
      try {
        const params = this.buildParams();
        this.appliedDailyWorkDate = params.dailyWorkDate || null;
        const res = await this.$http.get('/api/private/work-permit/list', { params });
        const data = (res && res.data && (res.data.payload || res.data)) || {};
        this.rows = data.rows || [];
        this.total = data.total || 0;
        this.page = data.page || this.page;
        this.pageSize = data.pageSize || this.pageSize;
      } catch (e) {
        console.log('work-permit list 조회 실패', e);
        this.noti('조회 오류', '목록을 불러오지 못했습니다.', 'danger');
      } finally {
        this.loading = false;
      }
    },
    onSearch() {
      this.page = 1;
      this.fetch();
    },
    resetSearch() {
      this.search = { keyword: '', status: '', idcCode: '' };
      this.dateRange = [];
      this.dailyWorkDate = '';
      this.page = 1;
      this.fetch();
    },
    goPage(p) {
      // 페이지 범위 보정 (10페이지 단위 이동 시 음수/초과 가능)
      const target = Math.max(1, Math.min(this.totalPages, p));
      if (target === this.page) return;
      this.page = target;
      this.fetch();
    },
    goDetail(r) {
      if (!r || !r.planId) return;
      this.$router.push({ path: '/app/workPermit/check', query: { planId: r.planId } });
    },
    idcNameOf(code) {
      if (!code) return '-';
      return this.locationMap[code] || code;
    },
    /**
     * 발주담당자 표시값. BE 가 IMDB 로 조회한 실명(createdByName)을 우선 사용하고,
     * 조회 실패 시에만 createdBy(이메일/사번)로 폴백한다.
     * 폴백 시 이메일은 로컬파트만 — 컬럼이 넓어지지 않게. 전체값은 title 로 노출.
     */
    ordererOf(r) {
      const name = String((r && r.createdByName) || '').trim();
      if (name) return name;
      const v = String((r && r.createdBy) || '').trim();
      if (!v) return '-';
      const noPrefix = v.replace(/^dev-/i, '');
      const at = noPrefix.indexOf('@');
      return at > 0 ? noPrefix.substring(0, at) : noPrefix;
    },
    parsedPayload(r) {
      if (!r || !r.payload) return null;
      // 매 호출마다 parse 비용을 줄이기 위해 캐시 키를 row 객체에 보관
      if (r.__parsedPayload !== undefined) return r.__parsedPayload;
      try {
        r.__parsedPayload = JSON.parse(r.payload);
      } catch (e) {
        r.__parsedPayload = null;
      }
      return r.__parsedPayload;
    },
    locationKeyToName(key) {
      // "SF0001|SF0004|SF0022|SF0136" → "각 세종 / 북관 / 1F / Test Bed 공조실"
      if (!key) return '';
      return String(key).split('|').map((c) => this.locationMap[c] || c).join(' / ');
    },
    locationSummary(r) {
      const p = this.parsedPayload(r);
      const keys = (p && p.selectedFloorKeys) || [];
      if (!keys.length) return '-';
      const first = this.locationKeyToName(keys[0]);
      return keys.length > 1 ? `${first} 외 ${keys.length - 1}건` : first;
    },
    locationCount(r) {
      const p = this.parsedPayload(r);
      return ((p && p.selectedFloorKeys) || []).length;
    },
    allLocationNames(r) {
      const p = this.parsedPayload(r);
      const keys = (p && p.selectedFloorKeys) || [];
      return keys.map((k) => this.locationKeyToName(k));
    },
    isLocationExpanded(r) {
      return !!(r && this.expandedLocations[r.planId]);
    },
    isDailyExpanded(r) {
      return !!(r && this.expandedDaily[r.planId]);
    },
    toggleDaily(r) {
      if (!r || !r.planId) return;
      const next = { ...this.expandedDaily };
      if (next[r.planId]) {
        delete next[r.planId];
        this.expandedDaily = next;
        return;
      }
      next[r.planId] = true;
      this.expandedDaily = next;
      if (this.dailyMap[r.planId] === undefined) this.loadDaily(r);
    },
    async loadDaily(r) {
      this.$set(this.dailyLoading, r.planId, true);
      try {
        const params = this.appliedDailyWorkDate ? { workDate: this.appliedDailyWorkDate } : {};
        const res = await this.$http.get('/api/public/daily-permit/by-parent/' + encodeURIComponent(r.planId), { params });
        const data = (res && res.data && (res.data.payload || res.data)) || [];
        this.$set(this.dailyMap, r.planId, this.withRevisions(Array.isArray(data) ? data : []));
      } catch (e) {
        console.log('daily-permit by-parent 조회 실패', e);
        this.$set(this.dailyMap, r.planId, []);
      } finally {
        this.$set(this.dailyLoading, r.planId, false);
      }
    },
    // 같은 작업일자끼리 묶어 차수(_rev) 부여 + 최신 여부(_isLatestRev) 표시.
    // 정렬: 작업일자 오름차순 → 같은 날짜는 생성순(오래된→최신). 최신 차수 = 그 날짜의 마지막.
    withRevisions(list) {
      const arr = (list || []).slice().sort((a, b) => {
        const wd = (Number(a.workDate) || 0) - (Number(b.workDate) || 0);
        if (wd !== 0) return wd;
        return (Number(a.createDate) || 0) - (Number(b.createDate) || 0);
      });
      const seq = {};
      arr.forEach((d) => {
        const key = d.workDate;
        seq[key] = (seq[key] || 0) + 1;
        d._rev = seq[key];
      });
      arr.forEach((d) => { d._revTotal = seq[d.workDate]; d._isLatestRev = d._rev === d._revTotal; });
      return arr;
    },
    formatDay(ms) {
      if (!ms) return '-';
      return moment(Number(ms)).format('YYYY-MM-DD');
    },
    openDailyDetail(d) {
      if (!d || !d.dailyPermitId) return;
      this.$router.push({ path: '/app/daily-permit/detail', query: { dailyPermitId: d.dailyPermitId } });
    },
    dailyPayload(d) {
      if (!d) return null;
      if (d.__dp !== undefined) return d.__dp;
      try {
        d.__dp = JSON.parse(d.payload || '{}');
      } catch (e) {
        d.__dp = null;
      }
      return d.__dp;
    },
    dailyTypeCount(d) {
      const p = this.dailyPayload(d);
      const n = ((p && p.types) || []).length;
      return n ? n + '건' : '-';
    },
    dailyTypeLabels(d) {
      const p = this.dailyPayload(d);
      const labels = ((p && p.types) || []).map((t) => t && t.label).filter(Boolean);
      return labels.length ? labels.join(', ') : '-';
    },
    dailyLocation(d) {
      const p = this.dailyPayload(d);
      return (p && (p.locationText || p.locationDetailText)) || '-';
    },
    dailyTimeSlots(d) {
      const p = this.dailyPayload(d);
      const ts = (p && p.common && p.common.timeSlots) || [];
      return ts.length ? ts.join(', ') : '-';
    },
    toggleLocation(r) {
      if (!r || !r.planId) return;
      // Vue 2 반응성 보장: 새 객체로 교체
      const next = { ...this.expandedLocations };
      if (next[r.planId]) delete next[r.planId];
      else next[r.planId] = true;
      this.expandedLocations = next;
    },
    formatPeriod(from, to) {
      if (!from && !to) return '-';
      const f = from ? moment(Number(from)).format('YYYY-MM-DD') : '';
      const t = to ? moment(Number(to)).format('YYYY-MM-DD') : '';
      return f && t ? `${f} ~ ${t}` : (f || t);
    },
    formatTs(ms) {
      if (!ms) return '-';
      return moment(Number(ms)).format('YYYY-MM-DD HH:mm');
    },
    statusLabel(s) {
      switch (s) {
        case 'DRAFT': return '임시저장';
        case 'SUBMITTED': return '검토 대기';
        case 'NTREE_PENDING': return 'NTREE 결재 중';
        case 'APPROVED': return '승인';
        case 'REJECTED': return '반려';
        default: return s || '-';
      }
    },
    statusTagClass(s) {
      switch (s) {
        case 'DRAFT': return 'line-type';
        case 'SUBMITTED': return 'warning';
        case 'NTREE_PENDING': return 'light-blue';
        case 'APPROVED': return 'success';
        case 'REJECTED': return 'danger';
        default: return 'line-type';
      }
    }
  }
};
</script>

<style scoped>
.search-item {
  margin-right: 6px;
  margin-bottom: 6px;
}
.mh-10 { margin-left: 10px; margin-right: 10px; }
.pv-30 { padding-top: 30px; padding-bottom: 30px; }

/* 작업 위치 셀 — 펼쳐보기 */
.wp-loc-cell {
  white-space: normal;
}
.wp-loc-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
}
.wp-loc-text {
  flex: 1;
  min-width: 0; /* flex item 안에서 ellipsis 동작하려면 min-width:0 필수 */
}
.wp-loc-list {
  margin: 0;
  padding-left: 0;
  list-style: none;
}
.wp-loc-list li {
  padding: 2px 0;
}
.wp-loc-toggle {
  flex-shrink: 0;
}
/* 하위 일일작업허가서 펼침 영역 */
.wp-daily-row > .wp-daily-cell {
  background: #f7f9fc;
  padding: 12px 16px;
}
.wp-daily-title {
  font-weight: 600;
  font-size: 12px;
  color: #333;
  margin-bottom: 8px;
}
.wp-daily-tbl {
  background: #fff;
}
.wp-daily-tbl th,
.wp-daily-tbl td {
  font-size: 12px;
}
.wp-daily-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.wp-daily-cnt {
  font-size: 11px;
}
/* 이전 리비전(재상신으로 대체된 건) — 흐리게 표시 */
.wp-daily-superseded td {
  color: #aaa;
  background: #fafafa;
}
</style>